import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSocket } from '../contexts/socketContext';
import { useAuth } from '../contexts/authContext'; // AuthContext 사용
import { API_URL, POLAR_URL } from '../lib/config'; // API_URL 사용
import './Inbox.css';

function Inbox() {
    const [invitations, setInvitations] = useState([]);
    const [chats, setChats] = useState([]);
    const [chatRoomInfo, setChatRoomInfo] = useState({ count: 0, limit: 3 });
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const socket = useSocket();
    const { currentUser } = useAuth();
    const currentUserId = localStorage.getItem('currentUserId');
    const [userEmail, setUserEmail] = useState('');

    // [추가 1] 프리미엄 구독 상태 관리
    const [subscriptionStatus, setSubscriptionStatus] = useState('none'); // 기본값 'none'
    const isPremium = subscriptionStatus === 'active'; // 'active'면 프리미엄 유저!

    // ✅ [수정] 성별 필터 변경 핸들러 (구독 여부에 따라 동작 분기)
    const handleGenderFilterChange = (e) => {
        const selectedOption = e.target.value;

        // '성별 무관' 선택 시 -> 그냥 통과
        if (selectedOption === 'none') return;

        // 프리미엄 옵션(남/녀)을 선택했을 때
        if (selectedOption === 'male' || selectedOption === 'female') {
            
            // 👑 1. 이미 구독 중인 유저라면? -> 결제창 안 띄우고 기능 적용!
            if (isPremium) {
                console.log(`프리미엄 필터 적용됨: ${selectedOption}`);
                // TODO: 실제 필터링 로직을 여기에 추가하거나, 상태(State)에 저장해서 API 호출 시 사용
                // alert("✨ 프리미엄 필터가 적용되었습니다!"); 
                return; 
            }

            // 🔒 2. 구독 안 한 유저라면? -> 결제 유도
            if (!currentUserId) {
                alert("로그인이 필요합니다.");
                e.target.value = 'none'; 
                return;
            }

            if (userEmail) {
                const checkoutUrl = `${POLAR_URL}?customer_email=${encodeURIComponent(userEmail)}`;
                window.open(checkoutUrl, '_blank');
            } else {
                // 혹시라도 이메일 로딩이 안 됐을 경우 대비 (새로고침 유도)
                alert("사용자 정보를 불러오는 중입니다. 잠시 후 다시 시도해주세요.");
            }
            
            // 결제하고 돌아오면 풀리니까 일단은 다시 되돌려둠
            e.target.value = 'none'; 
        }
    };

    useEffect(() => {
        if (!currentUser) return;

        // 페이지가 열릴 때 서버에서 실제 쪽지함 데이터를 가져오는 함수
        const fetchInboxData = async () => {

            setLoading(true);
            try {
                const idToken = await currentUser.getIdToken();
                const response = await fetch(`${API_URL}/api/inbox`, {
                    headers: { 'Authorization': `Bearer ${idToken}` }
                });
                if (!response.ok) {
                    throw new Error('쪽지함 데이터를 불러오지 못했습니다.');
                }
                const data = await response.json();
                // console.log('📬 [Inbox API] 서버로부터 받은 데이터:', data);
                setInvitations(data.invitations);
                setChats(data.chats);
                setChatRoomInfo({
                    count: data.chatRoomCount,
                    limit: data.chatRoomLimit,
                });

                if (data.email) {
                    setUserEmail(data.email);
                }

                // [추가] 서버에서 받은 구독 상태 저장
                // (주의: 백엔드 /api/inbox 응답에 subscriptionStatus 필드가 있어야 함!)
                if (data.subscriptionStatus) {
                    setSubscriptionStatus(data.subscriptionStatus);
                    console.log("내 구독 상태:", data.subscriptionStatus);
                }
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        };

        fetchInboxData();
    }, [currentUser]);

    useEffect(() => {
        if (!socket) return;

        const handleChatCreated = (newChat) => {
            setChats(prevChats => {
                // [추가] prevChats 배열에 newChat.id와 동일한 id를 가진 항목이 이미 있는지 확인합니다.
                const isAlreadyExist = prevChats.some(chat => chat.id === newChat.id);

                // 이미 존재한다면, 상태를 변경하지 않고 그대로 둡니다.
                if (isAlreadyExist) {
                    return prevChats;
                }

                // 존재하지 않을 때만 목록의 맨 앞에 추가합니다.
                return [newChat, ...prevChats];
            });

            // 채팅방 카운트는 항상 최신 길이를 반영하도록 수정하는 것이 더 안전합니다.
            // 하지만 위 로직으로도 대부분의 경우 잘 동작합니다.
            setChatRoomInfo(prev => {
                const newChatsCount = chats.some(chat => chat.id === newChat.id) ? chats.length : chats.length + 1;
                return { ...prev, count: newChatsCount };
            });
        };

        // 2. [추가] 새로운 초대(쪽지)가 왔을 때 실시간 처리
        const handleNewInvite = (data) => {
            // data 구조: { senderId, senderNickname, message, chatRoomId }
            // console.log("새로운 초대가 도착했습니다:", data);

            setInvitations(prev => {
                // 이미 목록에 있는지 확인 (중복 방지)
                if (prev.some(inv => inv.id === data.chatRoomId)) return prev;

                // 새 초대 객체 생성 (화면 표시용)
                const newInvite = {
                    id: data.chatRoomId,
                    partnerNickname: data.senderNickname || '알 수 없음',
                    lastMessage: data.message,
                    expiresIn: '방금 전' // 실시간 도착이므로 시간을 이렇게 표시하거나, 필요 시 계산 로직 추가
                };

                // 목록 맨 위에 추가
                return [newInvite, ...prev];
            });
        };

        socket.on('recommend_chat_created', handleChatCreated);
        socket.on('new_chat_invite', handleNewInvite);

        return () => {
            socket.off('recommend_chat_created', handleChatCreated);
            socket.off('new_chat_invite', handleNewInvite);
        };
    }, [socket, chats]);

    const handleAddChatClick = () => {
        navigate('/recommendations');
    };

    // [추가] AI 친구 찾기 버튼 핸들러
    const handleAddAIFriendClick = () => {
        // ▼▼▼ [임시 수정] 페이지 이동을 막고 안내 팝업 띄우기 ▼▼▼
        // alert('지금 AI 친구들이 오고 있어요! 정리가 끝나는 대로 오픈할게요!');
        
        // TODO: AI 친구 프로필을 보여주는 페이지나 컴포넌트로 이동
        navigate('/ai-friend'); // <--- 원래 이동하는 코드는 꼭 주석 처리!
    };

    const handleChatItemClick = (chatId) => {
        // 모든 아이템(chats, invitations)에서 해당 ID의 정보를 찾음
        const allItems = [...chats, ...invitations];
        const clickedItem = allItems.find(item => item.id === chatId);

        if (!clickedItem) {
            console.error('클릭된 채팅방 정보를 찾을 수 없습니다.');
            alert('채팅방 정보를 찾는 데 실패했습니다.');
            return;
        }

        // isPreview 없이 바로 ChatPage로 이동
        navigate('/chat', {
            state: {
                chatRoomId: chatId,
                partner: {
                    nickname: clickedItem.partnerNickname,
                    character: clickedItem.partnerCharacter
                },
                from: '/inbox'
            }
        });
    };

    // ▼▼▼ [수정] 이 함수는 이제 수락 처리 및 목록 업데이트만 합니다 ▼▼▼
    const handleAcceptInvitation = (e, invitationToAccept) => {
        e.stopPropagation(); // 아이템 전체 클릭 이벤트 막기

        if (!socket || !currentUserId) return alert('연결 정보가 없습니다.');

        // [구현 필요] 채팅방 개수 제한 확인 (프론트엔드)
        if (chatRoomInfo.count >= chatRoomInfo.limit) {
            alert(`대화 목록이 가득 찼습니다. (최대 ${chatRoomInfo.limit}개)`);
            return;
        }

        // console.log(`${invitationToAccept.partnerNickname}님의 초대를 수락 요청`);

        // 1. 서버에 초대 수락 이벤트를 보냅니다.
        socket.emit('accept_recommend_chat', {
             acceptorId: currentUserId,
             chatRoomId: invitationToAccept.id,
        });

        // 2. [낙관적 업데이트] 즉시 UI를 업데이트합니다.
        setInvitations(prev => prev.filter(inv => inv.id !== invitationToAccept.id));
        setChats(prev => {
            if (!prev.some(chat => chat.id === invitationToAccept.id)) {
                const acceptedChat = { ...invitationToAccept };
                delete acceptedChat.expiresIn;
                return [acceptedChat, ...prev];
            }
            return prev;
        });
        setChatRoomInfo(prev => ({ ...prev, count: prev.count + 1 }));

        // 3. 페이지 이동 로직은 제거되었습니다.
        // navigate('/chat', { state: { ... } });
    };
    // ▲▲▲ 여기까지 수정 ▲▲▲

    const renderNickname = (item) => {
        // 저장된 이름(customNickname)이 있으면: "저장명 (원래명)"
        if (item.customNickname && item.customNickname !== item.partnerNickname) {
            return (
                <span className="nickname-container">
                    <span className="custom-nick" style={{ fontWeight: 'bold' }}>
                        {item.customNickname}
                    </span>
                    <span className="orig-nick" style={{ fontSize: '0.85em', color: '#999', marginLeft: '6px', fontWeight: 'normal' }}>
                        ({item.partnerNickname})
                    </span>
                </span>
            );
        }
        // 없으면 그냥 "원래명"
        return <span className="nickname">{item.partnerNickname}</span>;
    };

    if (loading) {
        return <div className="inbox-container loading">로딩 중...</div>;
    }

    return (
        <div className="inbox-container">
            <button className="home-button-header" onClick={() => navigate('/')}>
                {'< 홈'}
            </button>
            <header className="inbox-header">
                <h1>쪽지함</h1>
            </header>

            <main className="inbox-main">
                {/* --- 대화 목록 섹션 (위로 이동) --- */}
                <section className="inbox-section">
                    <div className="chats-header">
                        <div className="chats-title-row">
                            <h2>대화 목록</h2>
                            <span className="chat-count-info">
                                ({chatRoomInfo.count}/{chatRoomInfo.limit})
                            </span>
                        </div>

                        {/* ✅ [수정] 프리미엄 상태에 따라 UI가 바뀌는 드롭다운 */}
                        {false && (
                            <div className="header-actions">
                                <select 
                                    className={`gender-filter-select ${isPremium ? 'premium-active' : ''}`}
                                    onChange={handleGenderFilterChange}
                                    defaultValue="none"
                                    style={isPremium ? { 
                                        border: '1px solid #FFD700', // ✨ 구독 중이면 금색 테두리
                                        color: '#FFD700' 
                                    } : {}}
                                >
                                    <option value="none">성별 무관</option>
                                    {/* 구독 중이면 자물쇠(🔒) 제거하고 가격 대신 'ON' 표시 */}
                                    <option value="male">
                                        {isPremium ? '남성만 (Premium ✨)' : '남성만 (🔒 $4.99)'}
                                    </option>
                                    <option value="female">
                                        {isPremium ? '여성만 (Premium ✨)' : '여성만 (🔒 $4.99)'}
                                    </option>
                                </select>
                            </div>
                        )}
                    </div>
                    
                    {/* [수정] 두 개의 버튼이 한 줄을 꽉 채우도록 새로운 컨테이너 추가 */}
                    <div className="action-buttons-row">
                        <button className="header-add-btn" onClick={handleAddChatClick}>
                            + AI 추천 상대 찾기
                        </button>
                        <button className="header-add-btn ai-friend-btn" onClick={handleAddAIFriendClick}>
                            + AI 친구 찾기
                        </button>
                    </div>

                    <div className="chats-list">
                        {chats.length > 0 ? (
                            // 1. 대화 목록이 있을 때: 기존처럼 맵핑해서 보여줌
                            chats.map(chat => (
                                <div
                                    key={chat.id}
                                    className={`inbox-item ${chat.unreadCount > 0 ? 'unread' : ''}`}
                                    onClick={() => handleChatItemClick(chat.id)}
                                >
                                    <div className="item-content">
                                        <span className="nickname">{renderNickname(chat)}</span>
                                        <p className="last-message">{chat.lastMessage}</p>
                                    </div>

                                    {chat.unreadCount > 0 && (
                                        <div className="unread-badge">
                                            {chat.unreadCount > 99 ? '99+' : chat.unreadCount}
                                        </div>
                                    )}
                                </div>
                            ))
                        ) : (
                            // 2. 대화 목록이 없을 때: 안내 메시지 표시
                            <p className="empty-message">진행 중인 대화가 없습니다.</p>
                        )}
                    </div>
                </section>
                
                {/* --- 받은 초대 목록 섹션 (아래로 이동) --- */}
                <section className="inbox-section">
                    <h2>새로운 초대</h2>
                    <div className="invitation-list">
                        {invitations.length > 0 ? (
                            invitations.map(invite => (
                                <div key={invite.id} className="inbox-item invitation" onClick={() => handleChatItemClick(invite.id)}>
                                    <div className="item-content">
                                        <span className="nickname">{renderNickname(invite)}</span>
                                        <p className="last-message">{invite.lastMessage}</p>
                                    </div>
                                    <span className="timer">{invite.expiresIn}</span>
                                    {/* ▼▼▼ [추가] '대화 시작' 버튼 ▼▼▼ */}
                                    <button 
                                        className="accept-button" 
                                        onClick={(e) => handleAcceptInvitation(e, invite)}
                                    >초대 수락</button>
                                </div>
                            ))
                        ) : (
                            <p className="empty-message">새로운 초대가 없습니다.</p>
                        )}
                    </div>
                </section>

            </main>
        </div>
    );
}

export default Inbox;